import { useState } from "react";
import { ChevronRight, Copy, Check, Link as LinkIcon } from "lucide-react";

export function DocEyebrow({ children }) {
  return <span className="eyebrow">{children}</span>;
}

export function DocH2({ id, children }) {
  return (
    <h2 id={id} className="group scroll-mt-28 pt-4 font-heading text-2xl font-normal tracking-tight text-welock-textPrimary">
      <a href={`#${id}`} className="inline-flex items-center gap-2">
        {children}
        <LinkIcon size={14} className="text-welock-textMuted opacity-0 transition-opacity group-hover:opacity-100" />
      </a>
    </h2>
  );
}

export function DocH3({ id, children }) {
  return (
    <h3 id={id} className="scroll-mt-28 font-heading text-lg text-welock-textPrimary">
      {children}
    </h3>
  );
}

export function DocP({ children }) {
  return <p className="text-[15px] font-light leading-7 text-welock-textSecondary">{children}</p>;
}

export function Lead({ children }) {
  return <p className="text-lg font-light leading-8 text-welock-textSecondary">{children}</p>;
}

const CALLOUT = {
  info: { bar: "border-l-welock-accent", tint: "bg-welock-accent/[0.04]", label: "text-welock-accent" },
  tip: { bar: "border-l-welock-accent", tint: "bg-welock-accent/[0.04]", label: "text-welock-accent" },
  warning: { bar: "border-l-welock-warning", tint: "bg-welock-warning/[0.05]", label: "text-welock-warning" },
  danger: { bar: "border-l-welock-danger", tint: "bg-welock-danger/[0.05]", label: "text-welock-danger" },
};

export function Callout({ type = "info", title, children }) {
  const c = CALLOUT[type] || CALLOUT.info;
  return (
    <div className={`border border-welock-border border-l-2 ${c.bar} ${c.tint} p-5`}>
      {title && <div className={`mb-1.5 font-mono text-[11px] uppercase tracking-widest ${c.label}`}>{title}</div>}
      <div className="text-sm font-light leading-6 text-welock-textSecondary">{children}</div>
    </div>
  );
}

export function Card({ icon: Icon, title, children, span = "" }) {
  return (
    <div className={`group border border-welock-border bg-welock-surface p-6 transition-colors hover:border-welock-hairline ${span}`}>
      {Icon && (
        <span className="mb-4 flex h-10 w-10 items-center justify-center border border-welock-border text-welock-textSecondary transition-colors group-hover:border-welock-accent/50 group-hover:text-welock-accent">
          <Icon size={17} />
        </span>
      )}
      {title && <div className="font-heading text-base text-welock-textPrimary">{title}</div>}
      {children && <div className="mt-1.5 text-sm font-light leading-6 text-welock-textSecondary">{children}</div>}
    </div>
  );
}

export function Grid({ cols = 2, children }) {
  const map = { 2: "sm:grid-cols-2", 3: "sm:grid-cols-2 lg:grid-cols-3", 4: "sm:grid-cols-2 lg:grid-cols-4" };
  return <div className={`grid grid-cols-1 gap-3 ${map[cols]}`}>{children}</div>;
}

export function Bento({ children }) {
  return <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 auto-rows-[minmax(120px,auto)]">{children}</div>;
}

export function Steps({ items }) {
  return (
    <ol className="relative ml-3 space-y-6 border-l border-welock-border pl-6">
      {items.map((it, i) => (
        <li key={i} className="relative">
          <span className="absolute -left-[31px] flex h-6 w-6 items-center justify-center border border-welock-accent/40 bg-welock-bg font-mono text-[11px] text-welock-accent">
            {i + 1}
          </span>
          <div className="font-heading text-base text-welock-textPrimary">{it.title}</div>
          <div className="mt-1 text-sm font-light leading-6 text-welock-textSecondary">{it.body}</div>
        </li>
      ))}
    </ol>
  );
}

export function Expandable({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-welock-border bg-welock-surface">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between px-5 py-3.5 text-left"
      >
        <span className="font-mono text-sm text-welock-textPrimary">{title}</span>
        <ChevronRight size={15} className={`text-welock-textMuted transition-transform ${open ? "rotate-90 text-welock-accent" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-welock-border px-5 py-4 text-sm font-light leading-6 text-welock-textSecondary">
          {children}
        </div>
      )}
    </div>
  );
}

export function CodeBlock({ code, lang = "bash" }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="group relative border border-welock-border bg-[#0c0e12]">
      <div className="flex items-center justify-between border-b border-welock-border px-4 py-2">
        <span className="font-mono text-[10px] uppercase tracking-widest text-welock-textMuted">{lang}</span>
        <button onClick={copy} data-testid="code-copy-button" aria-label="Copy code" className="text-welock-textMuted transition-colors hover:text-welock-accent">
          {copied ? <Check size={13} className="text-welock-accent" /> : <Copy size={13} />}
        </button>
      </div>
      <pre className="overflow-x-auto p-4 font-mono text-[12.5px] leading-6 text-welock-textSecondary">
        <code>{code}</code>
      </pre>
    </div>
  );
}

export function PropTable({ rows }) {
  return (
    <div className="overflow-hidden border border-welock-border">
      <div className="grid grid-cols-12 gap-2 border-b border-welock-border bg-welock-surface px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest text-welock-textMuted">
        <div className="col-span-3">Field</div>
        <div className="col-span-3">Type</div>
        <div className="col-span-6">Description</div>
      </div>
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 border-b border-welock-border/50 px-4 py-3 text-sm last:border-0">
          <div className="col-span-3 font-mono text-[12.5px] text-welock-accent">{r.field}</div>
          <div className="col-span-3 font-mono text-[12.5px] text-welock-textMuted">{r.type}</div>
          <div className="col-span-6 font-light text-welock-textSecondary">{r.desc}</div>
        </div>
      ))}
    </div>
  );
}

export function Pill({ children, tone = "accent" }) {
  const map = {
    accent: "border-welock-accent/30 bg-welock-accent/5 text-welock-accent",
    get: "border-welock-accent/30 bg-welock-accent/5 text-welock-accent",
    post: "border-welock-warning/30 bg-welock-warning/5 text-welock-warning",
    muted: "border-welock-border text-welock-textMuted",
  };
  return (
    <span className={`inline-flex items-center border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${map[tone]}`}>
      {children}
    </span>
  );
}
